<template>
  <fieldset>
    <legend>
      {{ $t('registrationForm.personalInfo') }}
    </legend>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }">
          {{ storeUser ? $t('registrationForm.firstNameAndSurnamePrefixNotRequired') : $t('registrationForm.firstNameAndSurnamePrefix') }}
        </label>
        <initials-input
          ref="firstNameInput"
          :value="customerDetails.firstName || ''"
          :errors="firstNameValidationErrors"
          name="firstName"
          @input="(v) => updateCustomerDetailsFirstName(v)"/>
        <text-input
          ref="surnamePrefixInput"
          :value="customerDetails.surnamePrefix || ''"
          :errors="surnamePrefixValidationErrors"
          :maxlength="8"
          name="surnamePrefix"
          @input="(v) => updateCustomerDetailsSurnamePrefix(v)"/>
        <registration-form-validation-messages :messages="firstNameValidationErrors"/>
        <registration-form-validation-messages :messages="surnamePrefixValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }">{{ $t('registrationForm.lastName') }}</label>
        <text-input
          ref="surnameInput"
          :value="customerDetails.surname || ''"
          :errors="surnameValidationErrors"
          :maxlength="50"
          name="surname"
          @input="(v) => updateCustomerDetailsSurname(v)"/>
        <registration-form-validation-messages :messages="surnameValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }">{{ $t('registrationForm.gender') }}</label>
        <select-input
          :value="customerDetails.salutation"
          :errors="salutationValidationErrors"
          :maxlength="50"
          :options="salutationOptions"
          name="gender"
          @input="(v) => updateCustomerDetailsProperty('salutation', v)"/>
        <registration-form-validation-messages :messages="salutationValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label for="customer-phoneNumber" class="optional">{{ $t('registrationForm.phoneNumber') }}</label>
        <intl-tel-field
          id="customer-phoneNumber"
          :value="customerDetails.phoneNumber"
          :errors="phoneNumberValidationErrors"
          name="customer[phoneNumber]"
          @change="(value) => updateCustomerDetailsProperty('phoneNumber', value)"
          @keydown="clearFieldValidationMessagesIfExists('phoneNumber')"/>
        <registration-form-validation-messages :messages="phoneNumberValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label class="optional">{{ $t('registrationForm.accountNumber') }}</label>
        <iban-field :value="customerDetails.bankAccountNumber || ''"
                    :name="'customer[accountNumber]'"
                    :id="'customer-accountNumber'"
                    :errors="bankAccountNumberValidationErrors"
                    :read-only="true"
                    @input="(v) => updateCustomerDetailsProperty('bankAccountNumber', v)"/>
        <tooltip :title="$t('registrationForm.accountNumberTooltip')" position="right"/>
        <registration-form-validation-messages :messages="bankAccountNumberValidationErrors"/>
      </div>
    </div>
  </fieldset>

</template>
<script>
/* @flow */
import IntlTelField from '../components/IntlTelField'
import IbanField from '../components/IbanField'
import uniqBy from 'lodash.uniqby'
import { mapState } from 'vuex'
import {
  CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH,
  CLEAR_ORDER_VALIDATION_ERRORS,
  SET_CUSTOMER_DETAILS_FIELD
} from '../store/modules/cart/mutation-types'
import {
  CLEAR_USER_VALIDATION_ERROR_BY_PROPERTY_PATH,
  SET_USER_PROFILE_FIELD
} from '../store/modules/user/mutation-types'
import TextInput from './registration-form/TextInput'
import InitialsInput from './registration-form/InitialsInput'
import SelectInput from './registration-form/SelectInput'
import RegistrationFormValidationMessages from '@/forms/registration-form/ValidationMessages'
import { stripDigits } from '../utils/string'
import { upperCaseFirst } from 'change-case'
import { SALUTATION_UNKNOWN, SALUTATION_MR, SALUTATION_MRS } from '../types/salutation'
import Tooltip from '../components/Tooltip'

const uniqByCode = arr => uniqBy(arr, (a) => a.code)

const mapValidationErrors = (propertyNames) => {
  return Object.assign({}, ...propertyNames.map(propertyName => (
    {
      [propertyName + 'ValidationErrors']: function () {
        return uniqByCode([
          ...this.orderValidationErrors.filter(error => error.propertyPath === 'order.customerDetails.' + propertyName),
          ...this.userValidationErrors.filter(error => error.propertyPath === 'order.profile.' + propertyName),
          ...this.userValidationErrors.filter(error => error.propertyPath === 'profile.' + propertyName)
        ])
      }
    }
  )))
}

export default {
  name: 'AbtCustomerDetailsForm',
  components: {
    Tooltip,
    RegistrationFormValidationMessages,
    IbanField,
    IntlTelField,
    TextInput,
    SelectInput,
    InitialsInput
  },
  props: {
    payInTerms: {
      type: Boolean,
      required: true
    },
    customerDetails: {
      type: Object,
      required: true
    },
    storeUser: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      salutationOptions: [
        { value: SALUTATION_UNKNOWN, text: this.$t('registrationForm.genderSelect') },
        { value: SALUTATION_MR, text: this.$t('registrationForm.genderM') },
        { value: SALUTATION_MRS, text: this.$t('registrationForm.genderF') }
      ]
    }
  },
  computed: {
    ...mapState({
      orderValidationErrors: state => state.cart.orderValidationErrors,
      userValidationErrors: state => state.user.userValidationErrors,
      userProfile: state => (state.user && state.user.user && state.user.user.profile ? state.user.user.profile : null)
    }),
    ...mapValidationErrors(['firstName', 'surnamePrefix', 'surname', 'salutation', 'emailAddress', 'phoneNumber', 'bankAccountNumber'])
  },
  mounted: function () {
    this.$store.commit('cart/' + CLEAR_ORDER_VALIDATION_ERRORS)
  },
  methods: {
    updateCustomerDetailsProperty (name, value) {
      this.clearFieldValidationMessagesIfExists(name)
      this.$store.commit('cart/' + SET_CUSTOMER_DETAILS_FIELD, { name, value })
      if (this.userProfile) {
        this.$store.commit('user/' + SET_USER_PROFILE_FIELD, { name, value })
      }
      this.$emit('property-changed', {name, value})
    },
    updateCustomerDetailsFirstName (value) {
      this.updateCustomerDetailsProperty('firstName', value)
      // always update the input field, even if the underlying data is not changed,
      // because it's possible that the value of the input (dom) element was changed
      this.$refs.firstNameInput.$forceUpdate()
    },
    updateCustomerDetailsSurnamePrefix (value) {
      this.updateCustomerDetailsProperty('surnamePrefix', stripDigits(value))
      this.$refs.surnamePrefixInput.$forceUpdate()
    },
    updateCustomerDetailsSurname (value) {
      this.updateCustomerDetailsProperty('surname', upperCaseFirst(stripDigits(value)))
      this.$refs.surnameInput.$forceUpdate()
    },
    clearFieldValidationMessagesIfExists (propertyName) {
      const fieldValidationMessages = this[propertyName + 'ValidationErrors']
      if (fieldValidationMessages && fieldValidationMessages.length > 0) {
        this.$store.commit('cart/' + CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH, 'order.customerDetails.' + propertyName)
        this.$store.commit('user/' + CLEAR_USER_VALIDATION_ERROR_BY_PROPERTY_PATH, 'order.profile.' + propertyName)
        this.$store.commit('user/' + CLEAR_USER_VALIDATION_ERROR_BY_PROPERTY_PATH, 'profile.' + propertyName)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/forms/AbtCustomerDetailsForm.vue